import { ApolloClient } from 'apollo-client';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { ApolloLink, split } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { onError } from 'apollo-link-error';
import { setContext } from 'apollo-link-context';
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';
import * as Sentry from '@sentry/browser';

import auth from '../services/Auth';
import introspectionQueryResultData from '../generated/fragmentTypes.json';

// fallback points to same host as client
const wsProtocol = window.location.protocol === 'http:' ? 'ws' : 'wss';
const wsUri =
  process.env.REACT_APP_DEFAULT_GRAPHQL_SOCKET_ENDPOINT ||
  `${wsProtocol}://${window.location.host}/subscriptions`;
const chatUri = process.env.REACT_APP_DEFAULT_GRAPHQL_HTTP_ENDPOINT || '/graphql';
const memberUri =
  process.env.REACT_APP_DEFAULT_WEBAPP_GRAPHQL_ENDPOINT || 'http://localhost:8000/graphql';

const sentryBreadCrumbLink = new ApolloLink((operation, forward) => {
  // add the request to sentry breadcrumb
  Sentry.addBreadcrumb({
    category: 'graphql',
    message: JSON.stringify(operation),
  });

  return forward(operation);
});

const createErrorLink = (kind) =>
  onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        // eslint-disable-next-line no-console
        console.error(
          `(${kind})[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );

        Sentry.withScope((scope) => {
          scope.setTag('error-type', kind);
          Sentry.captureException(new Error(message));
        });
      });
    }

    if (networkError) {
      // Report the underlying error if there's a non-json response (ServerParseError)
      // eslint-disable-next-line no-console
      console.error(`(${kind})[Network error]: ${networkError}`);
      const errorToReport = networkError.bodyText ? new Error(networkError.bodyText) : networkError;

      Sentry.withScope((scope) => {
        scope.setTag('error-type', kind);
        Sentry.captureException(errorToReport);
      });

      if (
        networkError.response &&
        networkError.response.statusText === 'Unauthorized' &&
        kind === 'GraphQL'
      ) {
        const currentPath = window.location.pathname;
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { store } = require('../config/Store');
        store.dispatch.user.logout({ nextUrl: `/login/?next=${currentPath}` });
      }
    }
  });

// network error handling
const errorLink = createErrorLink('GraphQL');
const wsErrorLink = createErrorLink('Subscription');

const authLink = setContext(async (_, { options }) => {
  const headers = { ...options };
  const authData = await auth.isAuthenticated();

  if (authData) {
    headers.Authorization = `${authData.token_type} ${authData.access_token}`;
  }

  return { headers };
});

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

export const createMemberClient = function () {
  const cache = new InMemoryCache({ addTypename: false, fragmentMatcher });

  const httpLink = createHttpLink({
    uri: memberUri,
  });

  const link = ApolloLink.from([sentryBreadCrumbLink, errorLink, authLink, httpLink]);

  return new ApolloClient({ link, cache });
};

const webSocketLink = new WebSocketLink({
  uri: wsUri,
  options: {
    lazy: true,
    reconnect: true,
    connectionParams: async () => {
      const authData = await auth.isAuthenticated();

      return {
        headers: {
          token: authData ? authData.access_token : '',
        },
      };
    },
  },
});

webSocketLink.subscriptionClient.onReconnected(() => {
  Sentry.captureMessage('Subscription client reconnected.');
});

const wsLink = ApolloLink.from([wsErrorLink, webSocketLink]);

const isWebSocketSubscription = ({ query }) => {
  const { kind, operation } = getMainDefinition(query);
  return kind === 'OperationDefinition' && operation === 'subscription';
};

export const closeWebsocket = () => {
  webSocketLink.subscriptionClient.close();
};

export const createChatClient = function () {
  const cache = new InMemoryCache({
    addTypename: false,
  });

  const httpLink = createHttpLink({
    uri: chatUri,
  });

  const xhrLink = ApolloLink.from([sentryBreadCrumbLink, errorLink, authLink, httpLink]);

  const link = split(isWebSocketSubscription, wsLink, xhrLink);

  return new ApolloClient({ link, cache });
};

export const memberClient = createMemberClient();
export const chatClient = createChatClient();
